import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchChangeOrderCatalog,
  createChangeOrder,
  submitCustomPriceRequest,
  approveChangeOrder,
  rejectChangeOrder,
  type ChangeOrder,
  type ChangeOrderItemInput,
  type UpsellCatalogItem,
} from '../lib/bridge';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { fieldControlSmClass } from '../components/ui/forms';

// Change Orders — the app's actual margin-floor enforcement layer for
// field-initiated upsells and reductions (docs/BUSINESS_RULES.md §3-7).
// Every create/approve/reject call goes straight through the unmodified
// appCreateChangeOrder/appApproveChangeOrderRequest/
// appRejectChangeOrderRequest — see the comment on `changeOrderCatalog` in
// src/V6Api.js for exactly what stays enforced (Field Lead discount
// ceilings, below-baseline Manager approval, etc).
export function ChangeOrderPanel({ jobId, changeOrders, canManage }: { jobId: string; changeOrders: ChangeOrder[]; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [showBuilder, setShowBuilder] = useState<'catalog' | 'custom' | null>(null);
  const [search, setSearch] = useState('');
  const [changeType, setChangeType] = useState<'Upsell' | 'Price Reduction'>('Upsell');
  const [reason, setReason] = useState('');
  const [items, setItems] = useState<(ChangeOrderItemInput & { name: string; unit: string; priceText: string; safeDiscountPct: number; maxFieldDiscountPct: number })[]>([]);
  const [customDescription, setCustomDescription] = useState('');
  const [customMeasurements, setCustomMeasurements] = useState('');
  const [customMaterials, setCustomMaterials] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approvedPrice, setApprovedPrice] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const catalogQuery = useQuery({
    queryKey: ['changeOrderCatalog', jobId, search],
    queryFn: () => fetchChangeOrderCatalog(jobId, search),
    enabled: showBuilder === 'catalog',
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['jobDetail'] });
  }

  const createMutation = useMutation({
    mutationFn: () => createChangeOrder({ jobId, changeType, reason, items: items.map(({ serviceId, qty, discountPct, details }) => ({ serviceId, qty, discountPct, details })) }),
    onSuccess: (result) => {
      if (result.ok) {
        setItems([]);
        setReason('');
        setShowBuilder(null);
        invalidate();
      }
    },
  });

  const customMutation = useMutation({
    mutationFn: () => submitCustomPriceRequest({ jobId, description: customDescription, measurements: customMeasurements, materials: customMaterials, reason }),
    onSuccess: (result) => {
      if (result.ok) {
        setCustomDescription('');
        setCustomMeasurements('');
        setCustomMaterials('');
        setShowBuilder(null);
        invalidate();
      }
    },
  });

  const approveMutation = useMutation({
    mutationFn: (input: { changeOrderId: string; approvedPrice?: number }) => approveChangeOrder(input),
    onSuccess: (result) => {
      if (result.ok) {
        setApprovingId(null);
        setApprovedPrice('');
        invalidate();
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (input: { changeOrderId: string; reason?: string }) => rejectChangeOrder(input),
    onSuccess: (result) => {
      if (result.ok) {
        setRejectingId(null);
        setRejectReason('');
        invalidate();
      }
    },
  });

  function addCatalogItem(item: UpsellCatalogItem) {
    setItems((prev) => [
      ...prev,
      { serviceId: item.serviceId, qty: item.defaultQty || 1, discountPct: 0, details: '', name: item.name, unit: item.unit, priceText: item.currentPriceText, safeDiscountPct: item.safeDiscountPct, maxFieldDiscountPct: item.maxFieldDiscountPct },
    ]);
  }

  function updateItem(i: number, patch: Partial<ChangeOrderItemInput>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }

  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  return (
    <Card>
      <CardHeader title="Change Orders" />

      {changeOrders.length === 0 && <EmptyState>No Change Orders yet.</EmptyState>}
      {changeOrders.length > 0 && (
        <ul className="space-y-2">
          {changeOrders.map((co) => (
            <li key={co.changeOrderId} className="rounded-xl border border-ink-border bg-ink p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-slate-200">
                  {co.changeType}
                  <Badge tone={co.status.toLowerCase().includes('approv') && !co.status.toLowerCase().includes('pending') ? 'success' : co.status.toLowerCase().includes('pending') ? 'gold' : 'neutral'}>
                    {co.status}
                  </Badge>
                </span>
                <span className="font-semibold text-gold-200">{co.netChangeText}</span>
              </div>
              {co.reason && <p className="mt-1 text-xs text-slate-500">{co.reason}</p>}
              {co.approvalUrl && co.status === 'Ready for Customer Approval' && <p className="mt-1 break-all text-xs text-gold-300">{co.approvalUrl}</p>}
              {canManage && (co.status === 'Pending Manager Approval' || co.status === 'Pending Manager Pricing') && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {approvingId === co.changeOrderId ? (
                    <>
                      {co.status === 'Pending Manager Pricing' && (
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          placeholder="Approved price"
                          className={`w-28 ${fieldControlSmClass}`}
                          value={approvedPrice}
                          onChange={(e) => setApprovedPrice(e.target.value)}
                        />
                      )}
                      <Button
                        variant="secondary"
                        busy={approveMutation.isPending}
                        busyLabel="Approving…"
                        onClick={() => approveMutation.mutate({ changeOrderId: co.changeOrderId, approvedPrice: approvedPrice ? Number(approvedPrice) : undefined })}
                      >
                        Confirm Approve
                      </Button>
                      <button type="button" onClick={() => setApprovingId(null)} className="text-xs text-slate-500 hover:text-slate-300">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setApprovingId(co.changeOrderId)} className="text-xs font-semibold text-emerald-300 hover:text-emerald-200">
                      Approve
                    </button>
                  )}
                  {rejectingId === co.changeOrderId ? (
                    <>
                      <input placeholder="Reason" className={`w-40 ${fieldControlSmClass}`} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      <Button variant="secondary" busy={rejectMutation.isPending} busyLabel="Rejecting…" onClick={() => rejectMutation.mutate({ changeOrderId: co.changeOrderId, reason: rejectReason })}>
                        Confirm Reject
                      </Button>
                      <button type="button" onClick={() => setRejectingId(null)} className="text-xs text-slate-500 hover:text-slate-300">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => setRejectingId(co.changeOrderId)} className="text-xs font-semibold text-red-300 hover:text-red-200">
                      Reject
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {approveMutation.data && !approveMutation.data.ok && <ErrorState message={approveMutation.data.message || 'Could not approve this Change Order.'} />}
      {rejectMutation.data && !rejectMutation.data.ok && <ErrorState message={rejectMutation.data.message || 'Could not reject this Change Order.'} />}

      {!showBuilder && (
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" onClick={() => setShowBuilder('catalog')}>
            New Change Order
          </Button>
          <Button variant="secondary" onClick={() => setShowBuilder('custom')}>
            Request Manager Price
          </Button>
        </div>
      )}

      {showBuilder === 'catalog' && (
        <div className="mt-3 space-y-3">
          <div className="flex items-end gap-2">
            <select className={fieldControlSmClass} value={changeType} onChange={(e) => setChangeType(e.target.value as 'Upsell' | 'Price Reduction')}>
              <option value="Upsell">Upsell</option>
              <option value="Price Reduction">Price Reduction</option>
            </select>
            <input className={`flex-1 ${fieldControlSmClass}`} placeholder="Search eligible services…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {catalogQuery.data?.message && <p className="text-xs text-gold-300">{catalogQuery.data.message}</p>}
          {catalogQuery.data?.ok && (catalogQuery.data.items?.length ?? 0) > 0 && (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {catalogQuery.data.items!.map((item) => (
                <li key={item.serviceId} className="flex items-center justify-between gap-2 rounded-lg border border-ink-border bg-ink-raised px-2.5 py-1.5 text-sm text-slate-200">
                  <span>
                    {item.name} — {item.currentPriceText}/{item.unit}
                  </span>
                  <button type="button" onClick={() => addCatalogItem(item)} className="text-xs font-semibold text-gold-300 hover:text-gold-200">
                    Add
                  </button>
                </li>
              ))}
            </ul>
          )}

          {items.length > 0 && (
            <ul className="space-y-2">
              {items.map((it, i) => (
                <li key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-ink-border bg-ink-raised p-2.5">
                  <span className="min-w-[8rem] flex-1 text-sm text-slate-200">{it.name}</span>
                  <div className="w-20 space-y-1">
                    <label className="text-[10px] font-semibold uppercase text-slate-500">Qty</label>
                    <input type="number" min="0.01" step="0.01" className={fieldControlSmClass} value={it.qty} onChange={(e) => updateItem(i, { qty: Number(e.target.value) })} />
                  </div>
                  <div className="w-24 space-y-1">
                    <label className="text-[10px] font-semibold uppercase text-slate-500">Discount %{it.safeDiscountPct ? ` (safe ≤${it.safeDiscountPct.toFixed(0)}%)` : ''}</label>
                    <input type="number" min="0" max={it.maxFieldDiscountPct} step="0.1" className={fieldControlSmClass} value={it.discountPct} onChange={(e) => updateItem(i, { discountPct: Number(e.target.value) })} />
                  </div>
                  <button type="button" onClick={() => removeItem(i)} className="text-xs font-semibold text-red-300 hover:text-red-200">
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {changeType === 'Price Reduction' && (
            <input className={fieldControlSmClass + ' w-full'} placeholder="Reason (required for a reduction)" value={reason} onChange={(e) => setReason(e.target.value)} required />
          )}

          {createMutation.data && !createMutation.data.ok && <ErrorState message={createMutation.data.message || 'Could not create the Change Order.'} />}

          <div className="flex gap-2">
            <Button busy={createMutation.isPending} busyLabel="Creating…" disabled={items.length === 0} onClick={() => createMutation.mutate()}>
              Create Change Order
            </Button>
            <Button variant="secondary" onClick={() => setShowBuilder(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {showBuilder === 'custom' && (
        <form
          className="mt-3 space-y-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            customMutation.mutate();
          }}
        >
          <textarea
            className={fieldControlSmClass + ' w-full'}
            placeholder="Describe the custom work that needs Manager pricing"
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            rows={2}
            required
          />
          <input className={fieldControlSmClass + ' w-full'} placeholder="Measurements" value={customMeasurements} onChange={(e) => setCustomMeasurements(e.target.value)} />
          <input className={fieldControlSmClass + ' w-full'} placeholder="Materials" value={customMaterials} onChange={(e) => setCustomMaterials(e.target.value)} />
          {customMutation.data && !customMutation.data.ok && <ErrorState message={customMutation.data.message || 'Could not submit this request.'} />}
          <div className="flex gap-2">
            <Button type="submit" busy={customMutation.isPending} busyLabel="Submitting…">
              Submit for Manager Pricing
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowBuilder(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
